import React, { useMemo, useState } from "react";
import Select from "react-select";
import { AnimatePresence, motion } from "framer-motion";
import { HiOutlineAdjustments, HiOutlineSearch, HiX } from "react-icons/hi";
import { HiOutlineChevronDown } from "react-icons/hi2";
import {
  FIELD_TYPES,
  buildActiveChips,
  countActiveFilters,
  fieldKeys,
} from "./filterUtils";

/* ------------------------------------------------------------------ *
 * Estilos do react-select alinhados ao restante do back-office
 * ------------------------------------------------------------------ */
const selectStyles = {
  control: (base, state) => ({
    ...base,
    minHeight: 38,
    borderRadius: 10,
    borderColor: state.isFocused ? "#94a3b8" : "#e2e8f0",
    boxShadow: "none",
    fontSize: 13,
    "&:hover": { borderColor: "#cbd5e1" },
  }),
  valueContainer: (base) => ({ ...base, padding: "1px 8px" }),
  placeholder: (base) => ({ ...base, color: "#94a3b8", fontSize: 13 }),
  multiValue: (base) => ({
    ...base,
    backgroundColor: "#f1f5f9",
    borderRadius: 6,
  }),
  multiValueLabel: (base) => ({ ...base, fontSize: 11, color: "#0f172a" }),
  option: (base, state) => ({
    ...base,
    fontSize: 13,
    backgroundColor: state.isSelected
      ? "#0f172a"
      : state.isFocused
      ? "#f1f5f9"
      : "transparent",
    color: state.isSelected ? "#fff" : "#0f172a",
  }),
  menuPortal: (base) => ({ ...base, zIndex: 999999 }),
  menu: (base) => ({ ...base, zIndex: 999999, fontSize: 13 }),
};

const FieldLabel = ({ children, hint }) => (
  <div className="flex items-baseline justify-between gap-2">
    <label className="text-[10px] font-semibold uppercase tracking-[0.2em] text-slate-400">
      {children}
    </label>
    {hint ? <span className="text-[10px] text-slate-400">{hint}</span> : null}
  </div>
);

const inputClass =
  "w-full rounded-[10px] border border-slate-200 bg-white px-3 py-2 text-[13px] text-slate-800 placeholder:text-slate-400 focus:border-slate-400 focus:ring-0";

/* ------------------------------------------------------------------ *
 * Um campo do schema
 * ------------------------------------------------------------------ */
const FilterField = ({ field, values, onChange }) => {
  const setValue = (key, value) => onChange({ ...values, [key]: value });

  switch (field.type) {
    case FIELD_TYPES.MULTISELECT: {
      const options = field.options || [];
      const selected = options.filter((option) =>
        (values[field.key] || []).some(
          (value) => String(value) === String(option.value)
        )
      );
      return (
        <div className="space-y-1">
          <FieldLabel hint={field.hint}>{field.label}</FieldLabel>
          <Select
            isMulti
            isLoading={field.isLoading}
            options={options}
            value={selected}
            styles={selectStyles}
            menuPortalTarget={
              typeof document !== "undefined" ? document.body : null
            }
            placeholder={field.placeholder || "Todos"}
            noOptionsMessage={() => "Nenhuma opção"}
            closeMenuOnSelect={false}
            onChange={(picked) =>
              setValue(
                field.key,
                (picked || []).map((option) => String(option.value))
              )
            }
          />
        </div>
      );
    }

    case FIELD_TYPES.SELECT:
    case FIELD_TYPES.BOOLEAN: {
      const options = field.options || [];
      return (
        <div className="space-y-1">
          <FieldLabel hint={field.hint}>{field.label}</FieldLabel>
          <select
            className={inputClass}
            value={values[field.key] ?? ""}
            onChange={(event) => setValue(field.key, event.target.value)}
          >
            {options.map((option) => (
              <option key={String(option.value)} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>
        </div>
      );
    }

    case FIELD_TYPES.DATE_RANGE:
      return (
        <div className="space-y-1">
          <FieldLabel hint={field.hint}>{field.label}</FieldLabel>
          <div className="flex items-center gap-1.5">
            <input
              type="date"
              className={inputClass}
              value={values[field.fromKey] || ""}
              onChange={(event) => setValue(field.fromKey, event.target.value)}
            />
            <span className="text-[10px] text-slate-400">até</span>
            <input
              type="date"
              className={inputClass}
              min={values[field.fromKey] || undefined}
              value={values[field.toKey] || ""}
              onChange={(event) => setValue(field.toKey, event.target.value)}
            />
          </div>
        </div>
      );

    case FIELD_TYPES.NUMBER_RANGE:
      return (
        <div className="space-y-1">
          <FieldLabel hint={field.hint}>{field.label}</FieldLabel>
          <div className="flex items-center gap-1.5">
            <input
              type="number"
              min={field.min}
              step={field.step}
              placeholder="mín"
              className={inputClass}
              value={values[field.fromKey] ?? ""}
              onChange={(event) => setValue(field.fromKey, event.target.value)}
            />
            <span className="text-[10px] text-slate-400">a</span>
            <input
              type="number"
              min={field.min}
              step={field.step}
              placeholder="máx"
              className={inputClass}
              value={values[field.toKey] ?? ""}
              onChange={(event) => setValue(field.toKey, event.target.value)}
            />
          </div>
        </div>
      );

    default:
      return (
        <div className="space-y-1">
          <FieldLabel hint={field.hint}>{field.label}</FieldLabel>
          <input
            type="text"
            className={inputClass}
            placeholder={field.placeholder || ""}
            value={values[field.key] ?? ""}
            onChange={(event) => setValue(field.key, event.target.value)}
          />
        </div>
      );
  }
};

/* ------------------------------------------------------------------ *
 * Painel completo
 * ------------------------------------------------------------------ */
const AdvancedFilters = ({
  title = "Filtros",
  subtitle,
  fields = [],
  values = {},
  onChange,
  onReset,
  searchKey = "search",
  searchPlaceholder = "Buscar...",
  quickFilters = [],
  resultsLabel,
  defaultOpen = true,
  extraActions = null,
}) => {
  const [isOpen, setIsOpen] = useState(defaultOpen);
  const [openGroups, setOpenGroups] = useState({});

  // Campos `inline` (ordenação, paginação) ficam na barra do topo; os demais
  // viram seções recolhíveis agrupadas por `group`.
  const inlineFields = useMemo(
    () => fields.filter((field) => field.inline),
    [fields]
  );

  const { groups, groupOrder } = useMemo(() => {
    const map = new Map();
    fields
      .filter((field) => field.key !== searchKey && !field.inline)
      .forEach((field) => {
        const group = field.group || "Geral";
        if (!map.has(group)) map.set(group, []);
        map.get(group).push(field);
      });
    return { groups: map, groupOrder: [...map.keys()] };
  }, [fields, searchKey]);

  const chipFields = useMemo(
    () => fields.filter((field) => !field.inline && !field.excludeFromChips),
    [fields]
  );
  const activeCount = countActiveFilters(chipFields, values);
  const chips = buildActiveChips(chipFields, values);

  const clearKeys = (keys) => {
    const next = { ...values };
    keys.forEach((key) => {
      next[key] = Array.isArray(values[key]) ? [] : "";
    });
    onChange(next);
  };

  const groupActiveCount = (groupFields) =>
    groupFields.reduce((total, field) => {
      const filled = fieldKeys(field).some((key) => {
        const value = values[key];
        return !(
          value === undefined ||
          value === null ||
          value === "" ||
          (Array.isArray(value) && value.length === 0)
        );
      });
      return filled ? total + 1 : total;
    }, 0);

  const isGroupOpen = (group, index) =>
    openGroups[group] ?? (index === 0 || groupActiveCount(groups.get(group)) > 0);

  return (
    <div className="rounded-2xl border border-slate-200 bg-white shadow-sm">
      {/* Cabeçalho */}
      <div className="flex flex-wrap items-center justify-between gap-3 border-b border-slate-200 px-4 py-3">
        <div className="flex items-center gap-2 text-slate-700">
          <span className="inline-flex h-8 w-8 items-center justify-center rounded-xl bg-slate-900 text-white">
            <HiOutlineAdjustments className="h-4 w-4" />
          </span>
          <div>
            <p className="text-[10px] uppercase tracking-[0.3em] text-slate-400">
              {title}
            </p>
            <p className="text-sm font-semibold text-slate-900">
              {subtitle}
              {activeCount > 0 && (
                <span className="ml-2 rounded-full bg-slate-900 px-2 py-0.5 text-[10px] font-semibold text-white">
                  {activeCount} ativo{activeCount > 1 ? "s" : ""}
                </span>
              )}
            </p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          {extraActions}
          <button
            type="button"
            onClick={onReset}
            disabled={activeCount === 0}
            className="rounded-lg border border-slate-200 px-3 py-1.5 text-xs font-medium text-slate-600 transition hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-40"
          >
            Limpar tudo
          </button>
          <button
            type="button"
            onClick={() => setIsOpen((prev) => !prev)}
            className="inline-flex items-center gap-1 rounded-lg border border-slate-200 px-3 py-1.5 text-xs font-medium text-slate-600 transition hover:bg-slate-50"
          >
            {isOpen ? "Recolher" : "Filtros avançados"}
            <HiOutlineChevronDown
              className={`h-3.5 w-3.5 transition-transform ${
                isOpen ? "rotate-180" : ""
              }`}
            />
          </button>
        </div>
      </div>

      {/* Busca + atalhos + ordenação */}
      <div className="flex flex-col gap-3 px-4 py-3 lg:flex-row lg:items-center">
        <div className="relative flex-1">
          <HiOutlineSearch className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
          <input
            type="search"
            className={`${inputClass} pl-9`}
            placeholder={searchPlaceholder}
            value={values[searchKey] || ""}
            onChange={(event) =>
              onChange({ ...values, [searchKey]: event.target.value })
            }
          />
        </div>
        {quickFilters.length > 0 && (
          <div className="flex flex-wrap items-center gap-1.5">
            {quickFilters.map((quick) => {
              const active = quick.isActive(values);
              return (
                <button
                  key={quick.id}
                  type="button"
                  onClick={() => onChange(quick.apply(values, active))}
                  className={`rounded-full border px-3 py-1 text-xs transition ${
                    active
                      ? "border-slate-900 bg-slate-900 text-white"
                      : "border-slate-200 bg-white text-slate-600 hover:bg-slate-50"
                  }`}
                >
                  {quick.label}
                </button>
              );
            })}
          </div>
        )}
        {inlineFields.map((field) => (
          <div key={field.key} className="lg:w-44 shrink-0">
            <FilterField field={field} values={values} onChange={onChange} />
          </div>
        ))}
      </div>

      {/* Grupos de filtros */}
      <AnimatePresence initial={false}>
        {isOpen && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.22 }}
            className="overflow-hidden border-t border-slate-100"
          >
            <div className="divide-y divide-slate-100">
              {groupOrder.map((group, index) => {
                const groupFields = groups.get(group);
                const open = isGroupOpen(group, index);
                const count = groupActiveCount(groupFields);
                return (
                  <div key={group} className="px-4 py-3">
                    <button
                      type="button"
                      onClick={() =>
                        setOpenGroups((prev) => ({ ...prev, [group]: !open }))
                      }
                      className="flex w-full items-center justify-between text-left"
                    >
                      <span className="text-[11px] font-semibold uppercase tracking-[0.25em] text-slate-500">
                        {group}
                        {count > 0 && (
                          <span className="ml-2 rounded-full bg-slate-100 px-2 py-0.5 text-[10px] text-slate-600">
                            {count}
                          </span>
                        )}
                      </span>
                      <HiOutlineChevronDown
                        className={`h-4 w-4 text-slate-400 transition-transform ${
                          open ? "rotate-180" : ""
                        }`}
                      />
                    </button>
                    {open && (
                      <div className="mt-3 grid gap-3 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
                        {groupFields.map((field) => (
                          <FilterField
                            key={field.key || field.fromKey}
                            field={field}
                            values={values}
                            onChange={onChange}
                          />
                        ))}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Chips do que está aplicado */}
      {chips.length > 0 && (
        <div className="flex flex-wrap items-center gap-1.5 border-t border-slate-100 px-4 py-2.5">
          <span className="text-[10px] uppercase tracking-[0.3em] text-slate-400">
            Aplicados
          </span>
          {chips.map((chip) => (
            <button
              key={chip.id}
              type="button"
              onClick={() => clearKeys(chip.keys)}
              className="inline-flex items-center gap-1 rounded-full bg-slate-100 px-2.5 py-1 text-[11px] text-slate-700 transition hover:bg-slate-200"
              title="Remover filtro"
            >
              <span className="font-medium">{chip.label}:</span>
              <span className="max-w-[220px] truncate">{chip.value}</span>
              <HiX className="h-3 w-3 text-slate-500" />
            </button>
          ))}
        </div>
      )}

      {resultsLabel && (
        <div className="border-t border-slate-100 px-4 py-2 text-xs text-slate-500">
          {resultsLabel}
        </div>
      )}
    </div>
  );
};

export default AdvancedFilters;
